import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const MySubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadSubscription();
  }, [user, navigate]);

  const loadSubscription = async () => {
    setLoading(true);
    const { subscription: subData } = await subscriptionService?.getUserSubscription(user?.id);
    setSubscription(subData);
    setLoading(false);
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Tem certeza que deseja cancelar sua assinatura? Você terá acesso até o final do período atual.')) {
      return;
    }

    setCanceling(true);
    const { error } = await subscriptionService?.cancelSubscription(subscription?.id);

    if (error) {
      alert('Erro ao cancelar assinatura. Tente novamente.');
    } else {
      alert('Assinatura cancelada com sucesso. Você terá acesso até ' + new Date(subscription?.current_period_end)?.toLocaleDateString('pt-BR'));
      loadSubscription();
    }
    setCanceling(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando assinatura...</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <Icon name="AlertCircle" size={64} className="text-gray-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Você não tem uma assinatura ativa
              </h1>
              <p className="text-gray-600 mb-8">
                Escolha um plano para começar a vender com estratégia.
              </p>
              <Button size="xl" onClick={() => navigate('/pricing')}>
                <Icon name="Zap" size={20} />
                Ver Planos
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Minha Assinatura</h1>
            <p className="text-gray-600">Gerencie sua assinatura e informações de pagamento</p>
          </div>

          {/* Current Plan Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Plano {subscription?.plan?.display_name}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    subscription?.status === 'active' ? 'bg-green-100 text-green-700' :
                    subscription?.status === 'canceled'? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {subscription?.status === 'active' ? 'Ativa' :
                     subscription?.status === 'canceled' ? 'Cancelada' :
                     subscription?.status}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-600">
                  R${subscription?.plan?.price_brl}
                </p>
                <p className="text-gray-600">/mês</p>
              </div>
            </div>

            {/* Billing Info */}
            <div className="border-t border-gray-200 pt-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Período atual:</span>
                <span className="font-semibold text-gray-900">
                  {formatDate(subscription?.current_period_start)} - {formatDate(subscription?.current_period_end)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Próxima cobrança:</span>
                <span className="font-semibold text-gray-900">
                  {subscription?.cancel_at_period_end ? 'Não haverá próxima cobrança' : formatDate(subscription?.current_period_end)}
                </span>
              </div>
              {subscription?.cancel_at_period_end && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    <Icon name="AlertTriangle" size={16} className="inline mr-2" />
                    Sua assinatura será cancelada em {formatDate(subscription?.current_period_end)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Features Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recursos Incluídos</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {subscription?.plan?.features?.filter(f => f?.included)?.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{feature?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Ações</h3>
            <div className="space-y-4">
              {!subscription?.cancel_at_period_end && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  fullWidth
                >
                  <Icon name="ArrowUpCircle" size={20} />
                  Fazer Upgrade do Plano
                </Button>
              )}
              {!subscription?.cancel_at_period_end ? (
                <Button
                  variant="danger"
                  onClick={handleCancelSubscription}
                  loading={canceling}
                  disabled={canceling}
                  fullWidth
                >
                  <Icon name="XCircle" size={20} />
                  Cancelar Assinatura
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/pricing')}
                  fullWidth
                >
                  <Icon name="RefreshCw" size={20} />
                  Reativar Assinatura
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySubscriptionPage;